import React, {
  useState,
  useEffect,
  ReactNode,
  createContext,
  useContext,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  Grid,
  IconButton,
  Drawer,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  Typography,
  Stack,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Sidebar } from "../Sidebar";
import { Header } from "../Header";
import type { HeaderMenuItem } from "../Header";
import { useSidebarWidth } from "../hooks/useSidebarWidth";
import { DASHBOARD_MAIN_CONTENT_TRANSITION } from "../layoutStability";

interface SidebarContextType {
  setSidebarCollapsed: (collapsed: boolean) => void;
  sidebarOpen: boolean;
}

const SidebarContext = createContext<SidebarContextType | null>(null);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    return null;
  }
  return context;
};

export interface DashboardLayoutHeaderProps {
  employeeId?: string;
  fullName?: string;
  onLogout?: () => void;
  onProfileClick?: () => void;
  profileImage?: string;
  menuItems?: HeaderMenuItem[];
  defaultMenuItems?: boolean;
  onNavigate?: (path: string) => void;
  propertiesData?: any;
  inquiryData?: any;
  propertiesInquiriesData?: any;
  employeeData?: any;
  breadcrumb?: React.ReactNode;
  theme?: {
    backgroundColor?: string;
    height?: number;
    borderColor?: string;
  };
  customContent?: React.ReactNode;
  showUserInfo?: boolean;
  customActions?: React.ReactNode;
}

export interface DashboardLayoutProps {
  headerProps?: DashboardLayoutHeaderProps;
  sidebarProps?: any;
  children?: ReactNode;
  logo?: React.ReactNode;
  showMobileHeader?: boolean;
  sidebarWidth?: {
    open: number;
    closed: number;
  };
  externalSidebarOpen?: boolean;
  externalForceCollapsed?: boolean;
  onSidebarOpenChange?: (open: boolean) => void;
  onSidebarCollapsedChange?: (collapsed: boolean) => void;
  /** 右コンテンツ領域の左 padding 上書き (MUI spacing)。未指定時は従来の自動値。 */
  contentPl?:
    | number
    | Partial<Record<"xs" | "sm" | "md" | "lg" | "xl", number>>;
  /** 右側に常設するペイン（AIチャット等）。左サイドバーの鏡像として固定表示する。 */
  rightPane?: React.ReactNode;
  /** 右ペインの幅(px)。指定時、コンテンツ領域をこの分だけ右に詰める。非スマホのみ有効。 */
  rightPaneWidth?: number;
  /** true の場合、右ペイン分の幅を確保せずメインコンテンツへ重ねて表示する。 */
  rightPaneOverlay?: boolean;
  /** true の場合、電話幅でも右ペインをアンマウントせず表示する。 */
  renderRightPaneOnPhone?: boolean;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  headerProps = {},
  sidebarProps = {},
  children,
  showMobileHeader = true,
  sidebarWidth = { open: 210, closed: 60 },
  externalSidebarOpen,
  externalForceCollapsed,
  onSidebarOpenChange,
  onSidebarCollapsedChange,
  contentPl,
  rightPane,
  rightPaneWidth = 0,
  rightPaneOverlay = false,
  renderRightPaneOnPhone = false,
}) => {
  const theme = useTheme();
  // isMobile: lg 未満 (<1200)。ドロワー表示とトグル=ドロワー開閉の判定に使用 (電話・タブレット共通)。
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  // isPhone: sm 未満 (<600)。ハンバーガーヘッダー / コンテンツ全幅 / モバイルユーザーメニューはこの幅のみ。
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  // isTablet: 600〜1199。常設の縮小レール (60px) を出し、トグルは push 展開でなく temporary ドロワーを開く。
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg"));
  const [internalDesktopSidebarOpen, setInternalDesktopSidebarOpen] =
    useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);
  const [internalForceCollapsed, setInternalForceCollapsed] = useState(false);
  const previousSidebarStateRef = React.useRef<boolean | null>(null);

  // 外部から制御される場合は外部の状態を使用、そうでなければ内部状態を使用
  const isExternallyControlled =
    externalSidebarOpen !== undefined && externalForceCollapsed !== undefined;
  const desktopSidebarOpen = isExternallyControlled
    ? externalSidebarOpen
    : internalDesktopSidebarOpen;
  const forceCollapsed = isExternallyControlled
    ? externalForceCollapsed
    : internalForceCollapsed;

  const setDesktopSidebarOpen = useCallback(
    (open: boolean) => {
      if (onSidebarOpenChange) {
        onSidebarOpenChange(open);
      } else {
        setInternalDesktopSidebarOpen(open);
      }
    },
    [onSidebarOpenChange],
  );

  const setForceCollapsed = useCallback(
    (collapsed: boolean) => {
      if (isExternallyControlled) {
        // 外部制御の場合は何もしない（外部でsetSidebarCollapsedを通じて管理される）
      } else {
        setInternalForceCollapsed(collapsed);
      }
    },
    [isExternallyControlled],
  );

  const setSidebarCollapsed = useCallback(
    (collapsed: boolean) => {
      if (onSidebarCollapsedChange) {
        // 外部で状態管理している場合は外部のコールバックを呼ぶ
        onSidebarCollapsedChange(collapsed);
      } else {
        // 内部で状態管理している場合
        if (collapsed) {
          // 閉じる前に現在の状態を保存
          if (previousSidebarStateRef.current === null) {
            previousSidebarStateRef.current = desktopSidebarOpen;
          }
          setForceCollapsed(true);
          setDesktopSidebarOpen(false);
        } else {
          // 元の状態に戻す
          setForceCollapsed(false);
          if (previousSidebarStateRef.current !== null) {
            setDesktopSidebarOpen(previousSidebarStateRef.current);
            previousSidebarStateRef.current = null;
          }
        }
      }
    },
    [
      desktopSidebarOpen,
      onSidebarCollapsedChange,
      setForceCollapsed,
      setDesktopSidebarOpen,
    ],
  );

  const effectiveSidebarOpen = forceCollapsed ? false : desktopSidebarOpen;

  useEffect(() => {
    if (!isMobile && !forceCollapsed) {
      if (!desktopSidebarOpen) {
        setDesktopSidebarOpen(true);
      }
      if (mobileSidebarOpen) {
        setMobileSidebarOpen(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, forceCollapsed]);

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileSidebarOpen(!mobileSidebarOpen);
    } else {
      setDesktopSidebarOpen(!desktopSidebarOpen);
    }
  };

  const closeMobileSidebar = () => {
    setMobileSidebarOpen(false);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleMenuItemClick = async (item: HeaderMenuItem) => {
    if (item.action === "logout") {
      if (headerProps.onLogout) {
        await headerProps.onLogout();
      }
      if (headerProps.onNavigate) {
        headerProps.onNavigate("/login");
      }
    } else if (item.link && item.link !== "#") {
      if (headerProps.onNavigate) {
        headerProps.onNavigate(item.link);
      }
    }

    if (item.onClick) {
      item.onClick();
    }

    handleCloseUserMenu();
  };

  // Get default menu items
  const getDefaultMenuItems = (userId?: string): HeaderMenuItem[] => [
    {
      title: "プロフィール",
      link: userId ? `/setting/accout/${userId}` : "/setting/accout",
      icon: null,
    },
    {
      title: "ログアウト",
      action: "logout",
      divider: true,
    },
  ];

  const finalMenuItems =
    headerProps.menuItems ||
    (headerProps.defaultMenuItems
      ? getDefaultMenuItems(headerProps.employeeId)
      : []);

  const calculatedSidebarWidth = isPhone
    ? 0
    : isTablet
      ? sidebarWidth.closed // タブレットは常に縮小レール (60px)
      : effectiveSidebarOpen
        ? sidebarWidth.open
        : sidebarWidth.closed;

  // Set CSS custom property for sidebar width
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--sidebar-width",
      `${calculatedSidebarWidth}px`,
    );
  }, [calculatedSidebarWidth]);

  const sidebarWidthValue = useSidebarWidth();
  // 右ペイン分の確保幅（非スマホかつ rightPane 指定時のみ）。
  const rightOffset =
    !isPhone && rightPane && !rightPaneOverlay ? rightPaneWidth : 0;

  const sidebarContextValue: SidebarContextType = useMemo(
    () => ({
      setSidebarCollapsed,
      sidebarOpen: effectiveSidebarOpen,
    }),
    [setSidebarCollapsed, effectiveSidebarOpen],
  );

  return (
    <SidebarContext.Provider value={sidebarContextValue}>
      {isMobile && (
        <Drawer
          anchor="left"
          open={mobileSidebarOpen}
          onClose={closeMobileSidebar}
          sx={{
            "& .MuiDrawer-paper": {
              width: sidebarWidth.open,
              boxSizing: "border-box",
            },
          }}
        >
          <Sidebar
            {...sidebarProps}
            sidebarOpen
            setSidebarOpen={(open) => {
              if (!open) {
                closeMobileSidebar();
              }
            }}
          />
        </Drawer>
      )}

      <Grid container height={"100vh"}>
        {!isPhone && (
          <Grid
            item
            width={sidebarWidthValue}
            sx={{
              position: "fixed",
              left: 0,
              top: 0,
              height: "100vh",
              zIndex: theme.zIndex.drawer,
              transition: "width 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <Sidebar
              // タブレットでは常に縮小レール固定。展開操作は temporary ドロワーで行う。
              sidebarOpen={isTablet ? false : effectiveSidebarOpen}
              setSidebarOpen={(open: boolean) => {
                if (isTablet) {
                  // その場で広げず、既存の temporary ドロワーを開く
                  setMobileSidebarOpen(open);
                } else if (!forceCollapsed) {
                  setDesktopSidebarOpen(open);
                }
              }}
              {...sidebarProps}
            />
          </Grid>
        )}

        <Grid
          item
          width={
            isPhone
              ? "100%"
              : `calc(100% - ${sidebarWidthValue}px - ${rightOffset}px)`
          }
          pr={{ lg: 3, xs: 3 }}
          pl={
            contentPl ??
            (isPhone ? { xs: 3 } : effectiveSidebarOpen ? 4 : { lg: 4 })
          }
          sx={{
            marginLeft: !isPhone ? `${sidebarWidthValue}px` : 0,
            ...(rightOffset > 0 && {
              height: "100vh",
              overflowY: "auto",
              overflowX: "hidden",
              overscrollBehaviorY: "contain",
            }),
            transition: DASHBOARD_MAIN_CONTENT_TRANSITION,
          }}
        >
          {showMobileHeader && isPhone && (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                zIndex: theme.zIndex.appBar,
                backgroundColor: theme.palette.background.paper,
                py: 1,
                px: 2,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <IconButton
                  onClick={toggleSidebar}
                  sx={{
                    color: theme.palette.text.primary,
                    padding: "2px",
                    borderRadius: "5px",
                    boxShadow: "0px 0px 8px 0px rgba(0, 0, 0, 0.15)",
                    "&:hover": {
                      backgroundColor: theme.palette.background.paper,
                    },
                  }}
                >
                  <MenuIcon
                    sx={{ color: theme.palette.primary.main, fontSize: 26 }}
                  />
                </IconButton>
                {headerProps.fullName && (
                  <Tooltip title="Open profile">
                    <Stack
                      direction="row"
                      alignItems="center"
                      onClick={handleOpenUserMenu}
                      sx={{ cursor: "pointer" }}
                    >
                      <IconButton
                        sx={{
                          p: 0.5,
                          mr: 0.6,
                          border: `1px solid ${theme.palette.divider}`,
                        }}
                      >
                        <Avatar
                          alt={headerProps.fullName || ""}
                          src={headerProps.profileImage}
                          sx={{ width: 25, height: 25 }}
                        >
                          {!headerProps.profileImage &&
                            headerProps.fullName.charAt(0)}
                        </Avatar>
                      </IconButton>
                    </Stack>
                  </Tooltip>
                )}
              </div>
            </Box>
          )}
          {!isPhone && (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: `${sidebarWidthValue}px`,
                right: `${rightOffset}px`,
                zIndex: theme.zIndex.appBar,
                width: `calc(100% - ${sidebarWidthValue}px - ${rightOffset}px)`,
                backgroundColor: theme.palette.background.paper,
                transition: "left 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            >
              <Box
                sx={{
                  px: { lg: 2, xs: 3 },
                  pl: effectiveSidebarOpen ? 2 : { lg: 2 },
                  pr: { lg: 3, xs: 3 },
                  transition: "padding-left 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                <Header
                  {...headerProps}
                  fixed={false}
                  theme={{
                    ...headerProps.theme,
                  }}
                />
              </Box>
            </Box>
          )}
          <Box
            sx={{
              pt: `${headerProps.theme?.height || 50}px`, // ヘッダー高さ分のpadding (md以上/未満で同一に揃える)
            }}
          >
            {children}
          </Box>
        </Grid>

        {/* 右ペイン（左サイドバーの鏡像として固定表示）。非スマホかつ rightPane 指定時のみ。 */}
        {(!isPhone || renderRightPaneOnPhone) &&
          rightPane &&
          rightPaneWidth > 0 && (
            <Box
              sx={{
                position: "fixed",
                right: 0,
                top: 0,
                height: "100vh",
                width: `${rightPaneWidth}px`,
                zIndex: theme.zIndex.drawer,
                borderLeft: rightPaneOverlay
                  ? "none"
                  : `1px solid ${theme.palette.divider}`,
                backgroundColor: rightPaneOverlay
                  ? "transparent"
                  : theme.palette.background.paper,
                pointerEvents: rightPaneOverlay ? "none" : "auto",
                "& > *": {
                  pointerEvents: "auto",
                },
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
            >
              {rightPane}
            </Box>
          )}
      </Grid>

      {/* Mobile User Menu */}
      {isPhone && (
        <Menu
          sx={{ mt: "45px" }}
          id="menu-appbar"
          anchorEl={anchorElUser}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(anchorElUser)}
          onClose={handleCloseUserMenu}
        >
          {finalMenuItems.map((item, index) => (
            <div key={index}>
              {item.divider && index > 0 && (
                <Box sx={{ borderTop: "1px solid #e0e0e0", my: 1 }} />
              )}
              <MenuItem onClick={() => handleMenuItemClick(item)}>
                {item.icon && (
                  <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
                    {item.icon}
                  </Box>
                )}
                <Typography sx={{ textAlign: "center" }}>
                  {item.title}
                </Typography>
              </MenuItem>
            </div>
          ))}
        </Menu>
      )}
    </SidebarContext.Provider>
  );
};
